import { add, isAfter } from 'date-fns'
import { cloneDeep } from 'lodash'
import React, { createContext, useReducer, useContext, ReactNode } from 'react'
import { ReturningPatient } from 'src/services/api/verifyPhone'

type AddVerifyInfo = {
  type: 'addVerifyInfo'
  payload: { phone: string; validUpon: string }
}

type SetInitialSmsSent = {
  type: 'setInitialSmsSent'
  payload: { phone: string }
}

type SetPhone = {
  type: 'setPhone'
  payload: { phone: string }
}

type SetLinkedPatients = {
  type: 'setLinkedPatients'
  payload: { linkedPatients: ReturningPatient[] }
}

type SelectLinkedPatient = {
  type: 'selectPatient'
  payload: { selectedPatient: ReturningPatient }
}

type SetVerified = {
  type: 'setVerified'
  payload: { phone: string; isVerified: boolean }
}

type ResetIsVerified = {
  type: 'resetIsVerified'
  payload: { phone: string }
}

type Action =
  | AddVerifyInfo
  | SetInitialSmsSent
  | SetPhone
  | SetLinkedPatients
  | SelectLinkedPatient
  | SetVerified
  | ResetIsVerified

type PhoneNumber = string
interface VerifyPhoneData {
  availableFrom?: string
  validUpon?: string
  initialSmsSent: boolean
  isVerified: boolean
}

type VerifyInfo = Record<PhoneNumber, VerifyPhoneData>

type State = {
  verifyInfo: VerifyInfo
  phone: string
  linkedPatients: ReturningPatient[]
  selectedPatient: ReturningPatient | undefined
}

type Dispatch = (action: Action) => void

const VerifyPhoneContext = createContext<State | undefined>(undefined)
const VerifyPhoneDispatchContext = createContext<Dispatch | undefined>(undefined)

const defaultState: State = {
  verifyInfo: {},
  phone: '',
  linkedPatients: [],
  selectedPatient: undefined,
}

const verifyPhoneReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'addVerifyInfo': {
      const { phone, validUpon } = action.payload
      const availableFrom = add(new Date(), { minutes: 3 }).toISOString()
      const verifyInfo = cloneDeep(state.verifyInfo)

      if (verifyInfo[phone]) {
        verifyInfo[phone] = { ...verifyInfo[phone], availableFrom, validUpon, initialSmsSent: true }
      } else {
        verifyInfo[phone] = { availableFrom, validUpon, initialSmsSent: true, isVerified: false }
      }

      return { ...state, verifyInfo }
    }
    case 'setInitialSmsSent': {
      const { phone } = action.payload
      const verifyInfo = cloneDeep(state.verifyInfo)
      if (!verifyInfo[phone]) {
        verifyInfo[phone] = { initialSmsSent: true, isVerified: false }
      }

      return { ...state, verifyInfo }
    }
    case 'setPhone': {
      const { phone } = action.payload
      return { ...state, phone }
    }
    case 'setLinkedPatients': {
      const { linkedPatients } = action.payload
      return { ...state, linkedPatients }
    }
    case 'selectPatient': {
      const { selectedPatient } = action.payload
      return { ...state, selectedPatient }
    }
    case 'setVerified': {
      const { phone, isVerified } = action.payload
      const verifyInfo = cloneDeep(state.verifyInfo)
      const verifyInfoForPhone = verifyInfo[phone]
      verifyInfoForPhone.isVerified = isVerified

      return { ...state, verifyInfo }
    }
    case 'resetIsVerified': {
      const { phone } = action.payload
      const verifyInfo = cloneDeep(state.verifyInfo)
      const verifyInfoForPhone = verifyInfo[phone]

      if (verifyInfoForPhone) {
        verifyInfoForPhone.isVerified = false
      }

      return { ...state, verifyInfo }
    }
  }
}

interface Props {
  children: ReactNode
}

export const VerifyPhoneProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(verifyPhoneReducer, { ...defaultState })

  return (
    <VerifyPhoneContext.Provider value={{ ...state }}>
      <VerifyPhoneDispatchContext.Provider value={dispatch}>{children}</VerifyPhoneDispatchContext.Provider>
    </VerifyPhoneContext.Provider>
  )
}

export const useVerifyPhoneContext = () => {
  const context = useContext(VerifyPhoneContext)
  if (context === undefined) {
    throw new Error('useVerifyPhoneState must be user within a VerifyPhoneProvider')
  }
  return context
}

export const useVerifyPhoneDispatch = () => {
  const context = useContext(VerifyPhoneDispatchContext)
  if (context === undefined) {
    throw new Error('useVerifyPhoneDispatch must be used within a VerifyPhoneProvider')
  }
  return context
}

export const getAvailableFromForPhone = (verifyInfo: VerifyInfo, phone: string) => {
  return verifyInfo[phone]?.availableFrom
}

export const canRevalidate = (verifyInfo: VerifyInfo, phone: string) => {
  const availableFrom = getAvailableFromForPhone(verifyInfo, phone)

  return availableFrom ? isAfter(new Date(), new Date(availableFrom)) : true
}

export const isVerified = (verifyInfo: VerifyInfo, phone: string) => !!verifyInfo[phone]?.isVerified

export const initialSmsSent = (verifyInfo: VerifyInfo, phone: string) => {
  return verifyInfo[phone]?.initialSmsSent
}

export const useIsExpired = (phone: string) => {
  const { verifyInfo } = useVerifyPhoneContext()
  const validUpon = verifyInfo[phone]?.validUpon

  return validUpon ? isAfter(new Date(), new Date(validUpon)) : false
}

export const addVerifyInfo = (phone: string, validUpon: string): AddVerifyInfo => ({
  type: 'addVerifyInfo',
  payload: { phone, validUpon },
})

export const setInitialSmsSent = (phone: string): SetInitialSmsSent => ({
  type: 'setInitialSmsSent',
  payload: { phone },
})

export const setPhone = (phone: string): SetPhone => ({
  type: 'setPhone',
  payload: { phone },
})

export const setLinkedPatients = (linkedPatients: ReturningPatient[]): SetLinkedPatients => ({
  type: 'setLinkedPatients',
  payload: { linkedPatients },
})

export const selectPatient = (selectedPatient: ReturningPatient): SelectLinkedPatient => ({
  type: 'selectPatient',
  payload: { selectedPatient },
})

export const setVerified = (phone: string, isVerified: boolean): SetVerified => ({
  type: 'setVerified',
  payload: { phone, isVerified },
})

export const resetIsVerified = (phone: string): ResetIsVerified => ({
  type: 'resetIsVerified',
  payload: { phone },
})
